import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDocumentTitle } from "../../../utils";
import { UnauthorizedError, clearStoredToken, getStoredToken, listRequests, listTaxonomy, login } from "./artGalleryApi";
import { SITE_URL } from "./artGalleryShared";
import ArtworksPanel from "./ArtworksPanel";
import TaxonomyPanel from "./TaxonomyPanel";
import ContentPanel from "./ContentPanel";
import RequestsPanel from "./RequestsPanel";
import "./ArtGallery.scss";

const TABS = [
  { id: "artworks", label: "Artworks" },
  { id: "artists", label: "Artists" },
  { id: "subjects", label: "Subjects" },
  { id: "pages", label: "Pages & settings" },
  { id: "requests", label: "Requests" },
];

const TAB_KEY = "artGalleryTab";

const BASE_PATH = "/art/dashboard";
const SECTIONS = [
  { id: "website", label: "Website", path: BASE_PATH },
  { id: "presentation", label: "Presentation", path: `${BASE_PATH}/presentation` },
];

const LoginGate = ({ onSignedIn }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!password.trim()) return;
    setBusy(true);
    try {
      await login(password.trim());
      onSignedIn();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ag-page ag-login">
      <form className="ag-login-card" onSubmit={submit}>
        <h1>Gallery</h1>
        <p>Enter the gallery password to manage the collection and website.</p>
        <input type="password" autoFocus placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button type="submit" className="ag-btn ag-btn-primary" disabled={busy}>
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const ArtGalleryDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const section = location.pathname.replace(/\/$/, "").endsWith("/presentation") ? "presentation" : "website";
  useDocumentTitle(section === "website" ? "Gallery" : "Presentation pieces");
  const [signedIn, setSignedIn] = useState(Boolean(getStoredToken()));
  const [tab, setTab] = useState(() => (TABS.some((t) => t.id === localStorage.getItem(TAB_KEY)) ? localStorage.getItem(TAB_KEY) : "artworks"));
  const [artists, setArtists] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [newRequests, setNewRequests] = useState(0);

  const handleUnauthorized = useCallback(() => setSignedIn(false), []);

  const handleError = useCallback(
    (error) => {
      if (error instanceof UnauthorizedError) handleUnauthorized();
      toast.error(error.message);
    },
    [handleUnauthorized]
  );

  useEffect(() => {
    if (!signedIn) return;
    Promise.all([listTaxonomy("artists"), listTaxonomy("subjects"), listRequests({ status: "new", limit: 1 })])
      .then(([nextArtists, nextSubjects, requests]) => {
        setArtists(nextArtists);
        setSubjects(nextSubjects);
        setNewRequests(requests.newCount);
      })
      .catch(handleError);
  }, [signedIn, handleError]);

  const selectTab = (id) => {
    setTab(id);
    localStorage.setItem(TAB_KEY, id);
  };

  const signOut = () => {
    clearStoredToken();
    setSignedIn(false);
  };

  if (!signedIn) return <LoginGate onSignedIn={() => setSignedIn(true)} />;

  const sectionSwitch = (
    <div className="ag-segmented" role="tablist" aria-label="Section">
      {SECTIONS.map((s) => (
        <button key={s.id} role="tab" aria-selected={section === s.id} className={section === s.id ? "is-active" : ""} onClick={() => navigate(s.path)}>
          {s.label}
        </button>
      ))}
    </div>
  );

  if (section === "presentation") {
    return (
      <div className="ag-page">
        <header className="ag-header">
          <div>
            <h1>Presentation pieces</h1>
            <p>Crop, size and mark each piece for the AI gallery presentation. Nothing here appears on the website.</p>
          </div>
          <div className="ag-header-actions">
            {sectionSwitch}
            <button className="ag-btn ag-btn-ghost" onClick={signOut}>
              Sign out
            </button>
          </div>
        </header>
        <ArtworksPanel key="presentation" section="presentation" onError={handleError} onUnauthorized={handleUnauthorized} />
      </div>
    );
  }

  return (
    <div className="ag-page">
      <header className="ag-header">
        <div>
          <h1>Gallery</h1>
          <p>Manage the CLK Art collection and website.</p>
        </div>
        <div className="ag-header-actions">
          {sectionSwitch}
          <a className="ag-btn ag-btn-ghost" href={SITE_URL} target="_blank" rel="noreferrer">
            View website ↗
          </a>
          <button className="ag-btn ag-btn-ghost" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <nav className="ag-tabs">
        {TABS.map((t) => (
          <button key={t.id} className={tab === t.id ? "is-active" : ""} onClick={() => selectTab(t.id)}>
            {t.label}
            {t.id === "requests" && newRequests > 0 && <span className="ag-tab-count">{newRequests}</span>}
          </button>
        ))}
      </nav>

      {tab === "artworks" && <ArtworksPanel artists={artists} subjects={subjects} onError={handleError} onUnauthorized={handleUnauthorized} />}
      {tab === "artists" && <TaxonomyPanel kind="artists" items={artists} onChange={setArtists} onError={handleError} />}
      {tab === "subjects" && <TaxonomyPanel kind="subjects" items={subjects} onChange={setSubjects} onError={handleError} />}
      {tab === "pages" && <ContentPanel artists={artists} onError={handleError} />}
      {tab === "requests" && <RequestsPanel onError={handleError} onCountChange={setNewRequests} />}
    </div>
  );
};

export default ArtGalleryDashboard;
